import { useState, useEffect, useRef } from 'react';
import { apiKeyService } from '../utils/api';
import { Plus, Copy, RefreshCw, Trash2, Key, CheckCircle, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';

const ApiKeysPage = () => {
  const [loading, setLoading] = useState(true);
  const [apiKeys, setApiKeys] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    // Small delay to ensure auth is ready
    const timer = setTimeout(() => {
      if (mountedRef.current) {
        loadApiKeys();
      }
    }, 200);
    
    return () => {
      mountedRef.current = false;
      clearTimeout(timer);
    };
  }, []);

  const loadApiKeys = async (retryCount = 0) => {
    try {
      const response = await apiKeyService.getAll();
      if (mountedRef.current) {
        setApiKeys(response.data.data);
      }
    } catch (error) {
      // Retry up to 2 times
      if (retryCount < 2 && mountedRef.current) {
        await new Promise(resolve => setTimeout(resolve, 500 * (retryCount + 1)));
        return loadApiKeys(retryCount + 1);
      }
      if (mountedRef.current) {
        toast.error('Failed to load API keys');
      }
      console.error(error);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    try {
      const response = await apiKeyService.create({ name: newKeyName });
      setCreatedKey(response.data.data);
      setNewKeyName('');
      setShowCreateModal(false);
      await loadApiKeys();
      toast.success('API key created successfully');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create API key');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('API key copied to clipboard');
  };

  const handleRevokeKey = async (keyId) => {
    if (!confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await apiKeyService.revoke(keyId);
      await loadApiKeys();
      toast.success('API key revoked successfully');
    } catch (error) {
      toast.error('Failed to revoke API key');
    }
  };

  const handleResetKey = async (keyId) => {
    if (!confirm('This will generate a new API key. The old key will stop working immediately.')) {
      return;
    }

    try {
      const response = await apiKeyService.reset(keyId);
      setCreatedKey(response.data.data);
      await loadApiKeys();
      toast.success('API key reset successfully');
    } catch (error) {
      toast.error('Failed to reset API key');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold mb-2">API Keys</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage your API keys for authentication
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          disabled={isCreating}
          className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-5 h-5" />
          <span>{isCreating ? 'Processing...' : 'Create New Key'}</span>
        </button>
      </div>

      {/* New Key Alert */}
      <AnimatePresence>
        {createdKey && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="card bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
          >
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold text-green-900 dark:text-green-100 mb-2">
                  API Key Created Successfully
                </h3>
                <p className="text-sm text-green-700 dark:text-green-300 mb-3">
                  Make sure to copy your API key now. You won't be able to see it again!
                </p>
                <div className="flex items-center space-x-2">
                  <code className="flex-1 bg-white dark:bg-dark-900 px-4 py-3 rounded-lg text-sm font-mono border border-green-300 dark:border-green-700">
                    {createdKey.apiKey}
                  </code>
                  <button
                    onClick={() => handleCopyKey(createdKey.apiKey)}
                    className="btn-secondary flex items-center space-x-2"
                  >
                    <Copy className="w-4 h-4" />
                    <span>Copy</span>
                  </button>
                </div>
              </div>
              <button
                onClick={() => setCreatedKey(null)}
                className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
              >
                ×
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* API Keys List */}
      {apiKeys.length === 0 ? (
        <div className="card text-center py-12">
          <Key className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No API Keys Yet</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Create your first API key to start using the service
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary inline-flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Create API Key</span>
          </button>
        </div>
      ) : (
        <div className="grid gap-6">
          {apiKeys.map((key) => (
            <div key={key._id} className="card">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold">{key.name}</h3>
                    <span className={`badge ${key.isActive ? 'badge-success' : 'badge-error'}`}>
                      {key.isActive ? 'Active' : 'Revoked'}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center space-x-2">
                      <Key className="w-4 h-4" />
                      <code className="bg-gray-100 dark:bg-dark-800 px-2 py-1 rounded">
                        {key.keyPrefix}
                      </code>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4" />
                      <span>Created {new Date(key.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-gray-600 dark:text-gray-400">Total Requests</div>
                      <div className="text-2xl font-bold">{key.usage?.totalRequests || 0}</div>
                    </div>
                    <div>
                      <div className="text-gray-600 dark:text-gray-400">Successful</div>
                      <div className="text-2xl font-bold text-green-600">
                        {key.usage?.successfulRequests || 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600 dark:text-gray-400">Failed</div>
                      <div className="text-2xl font-bold text-red-600">
                        {key.usage?.failedRequests || 0}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Rate Limit:</span>
                      <span className="ml-2 font-medium">
                        {key.rateLimit?.requestsPerMinute || 60} req/min
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Daily Limit:</span>
                      <span className="ml-2 font-medium">
                        {key.rateLimit?.requestsPerDay || 10000} req/day
                      </span>
                    </div>
                  </div>
                </div>

                {key.isActive && (
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => handleResetKey(key._id)}
                      className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                      title="Reset API Key"
                    >
                      <RefreshCw className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleRevokeKey(key._id)}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                      title="Revoke API Key"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="card max-w-md w-full"
            >
              <h2 className="text-2xl font-bold mb-4">Create API Key</h2>
              <form onSubmit={handleCreateKey} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Key Name
                  </label>
                  <input
                    type="text"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    placeholder="e.g., Production API Key"
                    className="input"
                    required
                  />
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Choose a descriptive name to identify this key
                  </p>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    disabled={isCreating}
                    className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    disabled={isCreating}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {isCreating ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Processing...</span>
                      </>
                    ) : (
                      <span>Create Key</span>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ApiKeysPage;
